import { TrashIcon } from '@calizahq/react-components-v2'
import { css } from '@emotion/react'
import type { Identifier, XYCoord } from 'dnd-core'
import { FC, PropsWithChildren, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { ReactComponent as MoveIcon } from '@/assets/icons/move.svg'

interface FilePreviewProps {
  file: File | { id: number; src: string }
  index: number
  onClickRemove(file: File | { id: number; src: string }): void
  onMoveFilePreview(d: number, h: number): void
  renderImage?: FC<{ src: string }>
  totalFiles: number
}

interface DragFilePreview {
  index: number
}

export function FilePreview({
  index,
  file,
  totalFiles,
  onMoveFilePreview,
  onClickRemove,
  renderImage: Image,
}: FilePreviewProps) {
  const ref = useRef<HTMLDivElement>(null)
  const src = (file as any)?.src ? (file as any).src : URL.createObjectURL(file as any)

  const [{ handlerId }, drop] = useDrop<DragFilePreview, void, { handlerId: Identifier | null }>({
    accept: 'FILE_PREVIEW',
    collect: (monitor: any) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover,
  })

  const [{ isDragging }, drag] = useDrag({
    type: 'FILE_PREVIEW',
    item: () => ({ index }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  function hover(item: DragFilePreview, monitor: any) {
    const dragIndex = item.index
    const hoverIndex = index
    const isSameItem = dragIndex === hoverIndex

    if (!ref.current || isSameItem) return

    const hoverBoundingRect = ref.current?.getBoundingClientRect()
    const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2
    const clientOffset = monitor.getClientOffset()
    const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left

    // * only replace swap index when the dragged card is past the halfway point of the hovered card
    const isDraggingLeft = dragIndex > hoverIndex
    const isDraggingRight = dragIndex < hoverIndex
    const isDragLeftPastHalfwayOfHover = isDraggingLeft && hoverClientX < hoverMiddleX
    const isDragRightPastHalfwayOfHover = isDraggingRight && hoverClientX > hoverMiddleX

    if (!isDragLeftPastHalfwayOfHover && !isDragRightPastHalfwayOfHover) return
    onMoveFilePreview(dragIndex, hoverIndex)
    item.index = hoverIndex
  }

  function handleClickTrash() {
    onClickRemove(file)
  }

  const filePreviewStyles = css`
    button {
      &:last-of-type {
        opacity: 0;
      }
    }

    &:hover {
      button {
        &:last-of-type {
          opacity: 1;
        }
      }
    }
  `

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-lg border-2 border-secondary"
      css={filePreviewStyles}
      data-handler-id={handlerId}
      ref={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
    >
      {Image ? <Image src={src} /> : <img alt="preview" className="w-full h-full object-cover" src={src} />}
      <ButtonWrapper className="top-2 bg-white">
        <TrashIcon className="w-4 icon-disabled" onClick={handleClickTrash} />
      </ButtonWrapper>

      {totalFiles > 1 && (
        <ButtonWrapper className="bottom-2">
          <MoveIcon className="w-4 icon-on-media" />
        </ButtonWrapper>
      )}
    </div>
  )
}

function ButtonWrapper({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return (
    <button
      className={`absolute right-2 flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-grey-500 hover:bg-grey-600 border border-grey-300 rounded-full transform active:scale-95 transition-opacity ${className}`}
      type="button"
    >
      {children}
    </button>
  )
}
